/**
 * Admin — Speedies (F-129; A plan §4.8). Super admins only: routes/admin.tsx gates the /admin
 * subtree (this page is not `support: true`), and every call checks `is_super_admin_internal` on
 * the server. With the `speedies` flag off the page shows nothing to act on.
 *
 * Review: the queue and one Speedy (`?id=`). Series and Lists are their own tabs.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { isSpeediesEnabled } from '@/lib/feature-flags';
import { SpeedyQueue, type AdminTab } from '@/components/admin/speedies/SpeedyQueue';
import { SpeedyAdminDetail } from '@/components/admin/speedies/SpeedyAdminDetail';
import { SpeedySeriesAdmin } from '@/components/admin/speedies/SpeedySeriesAdmin';
import { SpeedyListsAdmin } from '@/components/admin/speedies/SpeedyListsAdmin';
import { SpeedyAdminInfo } from '@/components/admin/speedies/SpeedyAdminInfo';

const searchSchema = z.object({
  view: z.enum(['review', 'series', 'lists']).optional(),
  queue: z.enum(['retag_risk', 'held', 'updates', 'reported', 'failed', 'retag', 'all']).optional(),
  id: z.string().uuid().optional(),
});

export const Route = createFileRoute('/admin/speedies')({
  validateSearch: searchSchema,
  component: AdminSpeedies,
});

function AdminSpeedies() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/admin/speedies' });
  const view = search.view ?? 'review';
  const queue: AdminTab = search.queue ?? 'held';
  const set = (patch: Partial<typeof search>) => void navigate({ search: (s) => ({ ...s, ...patch }), replace: true });

  if (!isSpeediesEnabled()) {
    return <p className="p-6 text-sm text-muted-foreground">Speedies are switched off (the speedies flag).</p>;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 md:p-6">
      <div className="flex items-center gap-1">
        <h1 className="text-xl font-semibold">Speedies</h1>
        <SpeedyAdminInfo />
      </div>
      <Tabs value={view} onValueChange={(v) => set({ view: v as typeof view })}>
        <TabsList>
          <TabsTrigger value="review">Review</TabsTrigger>
          <TabsTrigger value="series">Series</TabsTrigger>
          <TabsTrigger value="lists">Lists</TabsTrigger>
        </TabsList>
        <TabsContent value="review">
          <div className="grid gap-6 lg:grid-cols-[minmax(280px,380px)_1fr]">
            <div className={search.id ? 'hidden lg:block' : ''}>
              <SpeedyQueue tab={queue} onTab={(t) => set({ queue: t })} selectedId={search.id ?? null} onSelect={(id) => set({ id })} />
            </div>
            <div className="min-w-0">
              {search.id
                ? <SpeedyAdminDetail key={search.id} id={search.id} onClose={() => set({ id: undefined })} />
                : <p className="hidden text-sm text-muted-foreground lg:block">Pick a Speedy from the queue.</p>}
            </div>
          </div>
        </TabsContent>
        <TabsContent value="series"><SpeedySeriesAdmin /></TabsContent>
        <TabsContent value="lists"><SpeedyListsAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}
